import { useState } from "react";
import { FadeIn } from "@/components/FadeIn";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { CalendarCheck, Loader2, CheckCircle2 } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

const empty = { name: "", phone: "", email: "", vehicle_brand: "", vehicle_model: "", service: "", preferred_date: "", message: "" };

export const AppointmentSection = ({ content }) => {
  const { t, tc } = useLang();
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) { toast.error(t("appt.needFields")); return; }
    setLoading(true);
    try {
      await api.post("/appointments", form);
      toast.success(t("appt.successText"));
      setDone(true);
      setForm(empty);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="termin" data-testid="appointment-section" className="py-24 md:py-32 bg-white">
      <div className="container-x">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <FadeIn>
            <span className="overline">{t("appt.overline")}</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mt-3 mb-5">{t("appt.title")}</h2>
            <p className="text-slate-500 md:text-lg leading-relaxed mb-8">{t("appt.subtitle")}</p>
            <div className="space-y-4">
              {[t("appt.b1"), t("appt.b2"), t("appt.b3")].map((tx, i) => (
                <div key={i} className="flex items-center gap-3 text-slate-700">
                  <CheckCircle2 className="w-5 h-5 text-blue-600" /> {tx}
                </div>
              ))}
            </div>
          </FadeIn>

          <FadeIn delay={0.15}>
            <div className="card-light p-7 sm:p-9">
              {done ? (
                <div data-testid="appointment-success" className="text-center py-10">
                  <CheckCircle2 className="w-16 h-16 text-blue-600 mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">{t("appt.successTitle")}</h3>
                  <p className="text-slate-500 mb-6">{t("appt.successText")}</p>
                  <button onClick={() => setDone(false)} className="btn-ghost">{t("appt.another")}</button>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="appointment-form" className="space-y-4">
                  <div className="flex items-center gap-2 mb-2 text-blue-600"><CalendarCheck className="w-5 h-5" /><span className="font-semibold text-slate-900">{t("appt.formTitle")}</span></div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <input data-testid="appt-name" className="input-light" placeholder={t("appt.name")} value={form.name} onChange={set("name")} />
                    <input data-testid="appt-phone" className="input-light" placeholder={t("appt.phone")} value={form.phone} onChange={set("phone")} />
                  </div>
                  <input data-testid="appt-email" type="email" className="input-light" placeholder={t("appt.email")} value={form.email} onChange={set("email")} />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <input data-testid="appt-brand" className="input-light" placeholder={t("appt.brand")} value={form.vehicle_brand} onChange={set("vehicle_brand")} />
                    <input data-testid="appt-model" className="input-light" placeholder={t("appt.model")} value={form.vehicle_model} onChange={set("vehicle_model")} />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <select data-testid="appt-service" className="input-light" value={form.service} onChange={set("service")}>
                      <option value="">{t("appt.servicePh")}</option>
                      {(content.services || []).map((s, i) => <option key={i} value={s.title}>{tc(s.title)}</option>)}
                    </select>
                    <input data-testid="appt-date" type="date" className="input-light" value={form.preferred_date} onChange={set("preferred_date")} />
                  </div>
                  <textarea data-testid="appt-message" rows="3" className="input-light resize-none" placeholder={t("appt.message")} value={form.message} onChange={set("message")} />
                  <button data-testid="appt-submit" type="submit" disabled={loading} className="btn-blue w-full">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t("appt.submit")}
                  </button>
                </form>
              )}
            </div>
          </FadeIn>
        </div>
      </div>
    </section>
  );
};
